import { get } from '@libs/shared';
import forge from 'node-forge';
import { globalState } from '../models';

enum ALGORITHM_TYPE {
    RSA = 'rsa', // 通过rsa加密
    NOOP = 'noop' // 不加密
}

/**
 * PKCS#1 v1.5 填充相关常量
 */
const PKCS1_PADDING_TYPE = {
    /** 用于加密的填充类型 (前端公钥加密，后端私钥解密) */
    ENCRYPTION: 0x02,
    /** 用于签名的填充类型 (后端私钥签名，前端公钥验证) */
    SIGNATURE: 0x01
} as const;

/**
 * PKCS#1 填充格式中的特殊字节值
 */
const PKCS1_SPECIAL_BYTES = {
    /** PKCS#1 填充的起始标识符 */
    HEADER_START: 0x00,
    /** 填充字符串和消息之间的分隔符 */
    SEPARATOR: 0x00
} as const;

/**
 * RSA密钥相关的常量
 */
const RSA_CONSTANTS = {
    /** PKCS#1 v1.5 要求的最小填充长度 */
    MIN_PADDING_LENGTH: 8,
    /** RSA-OAEP 使用 SHA-1 时的开销字节数 */
    OAEP_SHA1_OVERHEAD: 42,
    /** PKCS#1 填充的头部长度 (0x00 + 填充类型) */
    PKCS1_HEADER_LENGTH: 2
} as const;

const handleRsaEncrypt = (pemKey: string, data: string) => {
    try {
        const publicKey = forge.pki.publicKeyFromPem(pemKey);
        const modulusLength = publicKey.n.bitLength();
        // 计算最大加密块大小（RSA-OAEP 的典型最大明文长度）
        const maxByteLength =
            Math.floor((modulusLength + 7) / 8) - RSA_CONSTANTS.OAEP_SHA1_OVERHEAD;

        // 将字符串转换为 UTF-8 字节数组
        const encoder = new TextEncoder();
        const dataBytes = encoder.encode(data);

        const encryptedBytes = forge.util.createBuffer();

        // 分段加密
        for (let i = 0; i < dataBytes.length; i += maxByteLength) {
            const chunk = dataBytes.slice(i, i + maxByteLength);
            // 将 Uint8Array 转换为 forge 的 ByteStringBuffer
            const chunkBuffer = forge.util.createBuffer(
                new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength) // 精确处理ArrayBuffer切片
            );
            const encryptedChunk = publicKey.encrypt(
                chunkBuffer.getBytes(), // 获取原始字节字符串
                'RSA-OAEP',
                {
                    md: forge.md.sha1.create(),
                    mgf: forge.mgf.mgf1.create(forge.md.sha1)
                }
            );
            encryptedBytes.putBytes(encryptedChunk);
        }
        return forge.util.encode64(encryptedBytes.getBytes());
    } catch (error) {
        return;
    }
};

/**
 * 使用rsa加密数据
 *
 * @param data 要加密的数据
 */
export const rsaEncrypt = (data: string): string => {
    const algorithm = get(globalState.options, 'pki.algorithm', '');
    const publicKey = get(globalState.options, 'pki.keys.public_key', '');
    if (algorithm === ALGORITHM_TYPE.RSA && publicKey) {
        const res = handleRsaEncrypt(publicKey, data);
        if (res) {
            // 加密成功
            return res;
        }
    }
    return data;
};

/**
 * 验证PKCS#1 v1.5签名填充格式
 *
 * @param paddedData 包含填充的解密数据
 * @returns 验证结果，即分隔符位置
 */
const validatePKCS1SignaturePadding = (paddedData: string) => {
    // 检查数据长度是否足够包含最小的填充结构
    if (
        paddedData.length <
        RSA_CONSTANTS.PKCS1_HEADER_LENGTH + RSA_CONSTANTS.MIN_PADDING_LENGTH + 1
    ) {
        return -1;
    }

    // 验证PKCS#1 v1.5签名填充格式: 0x00 || 0x01 || PS || 0x00 || M
    const firstByte = paddedData.charCodeAt(0);
    const secondByte = paddedData.charCodeAt(1);

    if (
        firstByte !== PKCS1_SPECIAL_BYTES.HEADER_START ||
        secondByte !== PKCS1_PADDING_TYPE.SIGNATURE
    ) {
        return -1;
    }

    // 查找填充字符串(PS)和消息(M)之间的分隔符 0x00
    let separatorIndex = -1;
    for (let i = RSA_CONSTANTS.PKCS1_HEADER_LENGTH; i < paddedData.length; i++) {
        if (paddedData.charCodeAt(i) === PKCS1_SPECIAL_BYTES.SEPARATOR) {
            separatorIndex = i;
            break;
        }
    }

    // 验证填充字符串长度是否符合PKCS#1标准
    const paddingStringLength = separatorIndex - RSA_CONSTANTS.PKCS1_HEADER_LENGTH;
    if (paddingStringLength < RSA_CONSTANTS.MIN_PADDING_LENGTH) {
        return -1;
    }

    return separatorIndex;
};

/**
 * 执行RSA公钥解密操作（用于验证签名）
 *
 * 注意：这里实际上是在验证由私钥签名的数据
 * 在RSA签名验证中，我们使用公钥来"解密"签名，恢复原始消息
 *
 * @param encryptedData 签名数据
 * @param publicKeyPem 公钥
 * @returns 解密后的原始消息
 */
const performRSASignatureVerification = (encryptedData: string, publicKeyPem: string) => {
    if (!encryptedData) {
        return;
    }
    try {
        // 将密文从Base64解码为二进制字符串
        const encryptedBytes = forge.util.decode64(encryptedData);

        // 加载RSA公钥
        const rsaPublicKey = forge.pki.publicKeyFromPem(publicKeyPem);

        // 计算RSA密钥的字节长度，这也是每个加密块的固定长度
        const rsaKeyByteLength = Math.ceil(rsaPublicKey.n.bitLength() / 8);

        const decryptedChunks: string[] = [];

        // 分块处理密文，每块长度等于RSA密钥长度
        for (let offset = 0; offset < encryptedBytes.length; offset += rsaKeyByteLength) {
            // 确保当前块的长度正确，RSA密文块长度必须严格等于密钥长度
            const remainingBytes = encryptedBytes.length - offset;
            if (remainingBytes < rsaKeyByteLength) {
                // 跳过长度不足的块，这通常表示数据损坏
                continue;
            }

            const encryptedChunk = encryptedBytes.slice(offset, offset + rsaKeyByteLength);

            // 使用公钥执行原始RSA数学运算（模幂运算）
            // 'NONE' 表示不添加任何新的填充，直接执行底层的RSA运算
            const decryptedChunkWithPadding = rsaPublicKey.encrypt(encryptedChunk, 'NONE');
            decryptedChunks.push(decryptedChunkWithPadding);
        }

        // 将所有解密的块连接起来
        const paddedData = decryptedChunks.join('');

        // 验证PKCS#1 v1.5签名填充
        const paddingValidation = validatePKCS1SignaturePadding(paddedData);

        if (paddingValidation === -1) {
            return;
        }

        // 提取原始消息（去除填充部分）
        const originalMessage = paddedData.substring(paddingValidation + 1);

        // 尝试将结果解码为UTF-8字符串
        return forge.util.decodeUtf8(originalMessage);
    } catch (error) {
        return;
    }
};

/**
 * 使用RSA公钥验证签名(即解密)
 *
 * @param encryptedData Base64编码的签名
 * @returns 恢复的原始消息
 */
export const rsaVerifySignature = (encryptedData: string): string | undefined => {
    const algorithm = get(globalState.options, 'pki.algorithm', '');
    const publicKey = get(globalState.options, 'pki.keys.public_key', '');

    if (algorithm === ALGORITHM_TYPE.RSA && publicKey) {
        const decryptedResult = performRSASignatureVerification(encryptedData, publicKey);

        return decryptedResult;
    }
};
